import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import BrandLogo from "@components/BrandLogo";
import _ from "lodash";
import DOMPurify from "dompurify";

export default function Spotlight({
  currentQuestion,
  setCurrentQuestion,
  setAnswers,
  answers,
  sendAnswers,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);
  const XGA = width >= 1020;
  const [found, setFound] = useState(false);

  const canvasRef = useRef(null);
  const ctx = useRef(null);
  const canvasWidth = useRef(null);
  const canvasHeight = useRef(null);
  const image = useRef(null);
  const spotLightRadius = useRef(0);
  const polygonVertices = useRef();

  const checkIfSpottedDebounced = useRef(_.debounce(checkIfSpotted, 300));

  function checkIfSpotted(
    polygonVertices,
    ctx,
    cx,
    cy,
    radius,
    canvasWidth,
    canvasHeight,
    image
  ) {
    const hit = isPolygonInsideCircle(polygonVertices, cx, cy, radius);

    if (!hit) {
      return;
    }

    animateSpotlight(ctx, cx, cy, radius, canvasWidth, canvasHeight, image);
    setFound(true);
    return;
  }

  function transfromPolygonStringToVertices(
    polygonString,
    canvasWidth,
    canvasHeight
  ) {
    const openBracketIndex = polygonString.indexOf("(");
    const closeBracketIndex = polygonString.indexOf(")");
    const innerValues = polygonString.substring(
      openBracketIndex + 1,
      closeBracketIndex
    );

    const splitValues = innerValues
      .split(",")
      .map((value) => value.trim().split(" "));

    return splitValues.map(([x, y]) => {
      return [
        parseFloat(x) * 0.01 * canvasWidth,
        parseFloat(y) * 0.01 * canvasHeight,
      ];
    });
  }

  function isPolygonInsideCircle(polygonVertices, cx, cy, circleRadius) {
    for (let i = 0; i < polygonVertices.length; i++) {
      const [vertexX, vertexY] = polygonVertices[i];
      const distance = Math.sqrt(
        Math.pow(vertexX - cx, 2) + Math.pow(vertexY - cy, 2)
      );

      if (distance > circleRadius) {
        return false;
      }
    }
    return true;
  }

  function draw(ctx, cx, cy, radius, canvasWidth, canvasHeight, image) {
    if (!image || !image.width || !image.height) return; // ← zabezpieczenie

    ctx.save();
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    const radialGradient = ctx.createRadialGradient(cx, cy, 1, cx, cy, radius);
    radialGradient.addColorStop(0, "rgba(0,0,0,1)");
    radialGradient.addColorStop(0.65, "rgba(0,0,0,1)");
    radialGradient.addColorStop(1, "rgba(0,0,0,0)");
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = radialGradient;
    ctx.fill();
    ctx.globalCompositeOperation = "source-atop";
    ctx.drawImage(
      image,
      0,
      0,
      image.width,
      image.height,
      0,
      0,
      canvasWidth,
      canvasHeight
    );
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "#1E1E1E";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    ctx.restore();
  }

  function animateSpotlight(
    ctx,
    cx,
    cy,
    radius,
    canvasWidth,
    canvasHeight,
    image
  ) {
    const maxRadius = Math.max(canvasWidth, canvasHeight);

    function animate() {
      draw(ctx, cx, cy, radius, canvasWidth, canvasHeight, image);

      radius *= 1.05;

      if (radius < maxRadius) {
        requestAnimationFrame(animate);
      }
    }
    animate();
  }

  function handleMouseMove(e) {
    if (found || !image.current || !image.current.width) return;

    e.preventDefault();
    e.stopPropagation();

    draw(
      ctx.current,
      e.nativeEvent.offsetX,
      e.nativeEvent.offsetY,
      spotLightRadius.current,
      canvasWidth.current,
      canvasHeight.current,
      image.current
    );

    checkIfSpottedDebounced.current(
      polygonVertices.current,
      ctx.current,
      e.nativeEvent.offsetX,
      e.nativeEvent.offsetY,
      spotLightRadius.current,
      canvasWidth.current,
      canvasHeight.current,
      image.current
    );
  }

  function handleTouchMove(e) {
    if (found || !image.current || !image.current.width) return;

    e.stopPropagation();

    const { clientX, clientY } = e.nativeEvent.targetTouches[0];
    const rect = canvasRef.current.getBoundingClientRect();

    const cx = clientX - rect.x;
    const cy = clientY - rect.y;

    draw(
      ctx.current,
      cx,
      cy,
      spotLightRadius.current,
      canvasWidth.current,
      canvasHeight.current,
      image.current
    );

    checkIfSpottedDebounced.current(
      polygonVertices.current,
      ctx.current,
      cx,
      cy,
      spotLightRadius.current,
      canvasWidth.current,
      canvasHeight.current,
      image.current
    );
  }

  function resetSpotlightState() {
    setFound(false);
    polygonVertices.current = null;
    image.current = null;
    ctx.current?.clearRect?.(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height
    );
  }

  function handleNextButtonClick() {
    resetSpotlightState();

    setAnswers((prev) => [
      ...prev,
      {
        question_ident: `q${currentQuestion}`,
        answer_ident: "",
      },
    ]);
    setCurrentQuestion((prev) =>
      prev <= gameQuestionsQuery.content.length - 1 ? ++prev : prev
    );
  }

  useEffect(() => {
    if (!canvasRef.current) return;

    // Reset everything
    setFound(false);
    polygonVertices.current = null;
    image.current = null;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    ctx.current = context;

    canvasWidth.current = canvas.clientWidth;
    canvasHeight.current = canvas.clientHeight;
    spotLightRadius.current = canvasWidth.current * 0.15;

    context.canvas.width = canvasWidth.current;
    context.canvas.height = canvasHeight.current;

    const question = gameQuestionsQuery.content[currentQuestion];
    const polygon = question.answers[width < 1020 ? "mobile" : "desktop"];
    const imgSrc = question[width < 1020 ? "mobileImg" : "desktopImg"];

    polygonVertices.current = transfromPolygonStringToVertices(
      polygon,
      canvasWidth.current,
      canvasHeight.current
    );

    image.current = new Image();
    image.current.onload = () => {
      draw(
        context,
        -200,
        -200,
        spotLightRadius.current,
        canvasWidth.current,
        canvasHeight.current,
        image.current
      );
    };
    image.current.src = imgSrc;
  }, [width, currentQuestion]);

  return (
    <section className={`game-page spotlight-mechanics ${ident}`}>
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>
        <div className="main-game-wrppaer">
          <div className="desktop-wrapper">
            <LinearGradientProgressBar
              value={currentQuestion / gameQuestionsQuery.content.length}
            />
            <div className="game-question">
              <h2
                className="question"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(gameQuestionsQuery.content[currentQuestion].question),
                }}
              />
            </div>
          </div>
          <div className="side-img">
            <BrandLogo brands={gameQuestionsQuery?.brands} />
            <canvas
              style={{
                aspectRatio: `${
                  gameQuestionsQuery.content[currentQuestion][
                    `${width < 1020 ? "mobile" : "desktop"}AspectRatio`
                  ]
                }`,
              }}
              ref={canvasRef}
              onMouseMove={handleMouseMove}
              onTouchMove={handleTouchMove}
            />
            {XGA && (
              <StickyContent>
                <div className="desktop-buttons">
                  <Button disabled={!found} action={handleNextButtonClick}>
                    {gameQuestionsQuery.content[currentQuestion]
                      .nextButtonCopy ?? "Nächste"}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
        </div>
      </div>

      {!XGA && (
        <StickyButton disabled={!found} action={handleNextButtonClick}>
          {gameQuestionsQuery.content[currentQuestion].nextButtonCopy ??
            "Nächste"}
        </StickyButton>
      )}
    </section>
  );
}
